import { isBrowser } from '../../window';
import { isJsonString } from '../../string/isJsonString';
import { JENTIS_PAGE_TYPES } from './constants';
import { PixumEnvUtils } from '../../pixumEnv';
import type { JentisPayloadData } from './types';

export const isJentis = (): boolean =>
  isBrowser() && typeof window._jts !== 'undefined' && typeof window.jentis?.tracker !== 'undefined';

/**
 * Retrieves the Jentis tracker controller document IDs.
 *
 * This function checks if the Jentis tracker is available and attempts to access the `oCache.jentis_raw_controller_documentids` property.
 * The expected return type is an object containing various IDs related to the Jentis tracker, such as:
 *
 * {
 *   "event": {
 *     "_id": "1234"
 *   },
 *   "user": {
 *     "_id": "12345"
 *   },
 *   "session": {
 *     "_id": "123456"
 *   },
 *   "consent": {
 *     "_id": "1234567"
 *   }
 * }
 *
 * If the Jentis tracker is not available, the function returns `undefined`.
 *
 * @returns An object containing the Jentis tracker controller document IDs, or `undefined` if unavailable.
 */
function getJentisTrackerRawControllerDocumentIds() {
  if (!isJentis()) return;

  const tracker = window.jentis?.tracker;

  return tracker?.oCache?.jentis_raw_controller_documentids || undefined;
}

/**
 * Retrieves the Jentis User ID from the tracker controller document IDs.
 *
 * This function checks if the Jentis tracker is available and attempts to access the `oCache.jentis_raw_controller_documentids` property.
 * Specifically, it retrieves the `_id` associated with the `user` object within the document IDs.
 *
 * If the User ID is successfully retrieved, it is returned as a string.
 * If the User ID is unavailable, a warning is logged to the console, and an empty string is returned.
 *
 * @returns {string} The Jentis User ID if available, otherwise an empty string.
 */
export const getJentisUserId = (): string | undefined => {
  if (!isJentis()) return;

  const controllerDocumentIds = getJentisTrackerRawControllerDocumentIds();
  const jentisUserId = controllerDocumentIds?.user?._id;

  if (jentisUserId) {
    return jentisUserId;
  }

  console.warn(
    `Could not read Jentis User Id from ${window.jentis?.tracker?.oCache?.jentis_raw_controller_documentids}`,
  );

  return '';
};

/**
 * Retrieves the Jentis Session ID from the tracker controller document IDs.
 *
 * This function checks if the Jentis tracker is available and attempts to access the `oCache.jentis_raw_controller_documentids` property.
 * Specifically, it retrieves the `_id` associated with the `session` object within the document IDs.
 *
 * If the Session ID is successfully retrieved, it is returned as a string.
 * If the Session ID is unavailable, a warning is logged to the console, and an empty string is returned.
 *
 * @returns The Jentis Session ID if available, otherwise an empty string.
 */
export function getJentisSessionId() {
  if (!isJentis()) return;

  const controllerDocumentIds = getJentisTrackerRawControllerDocumentIds();
  const jentisSessionId = controllerDocumentIds?.session?._id;

  if (jentisSessionId) {
    return jentisSessionId;
  }

  console.warn(
    `Could not read Jentis Session Id from ${window.jentis?.tracker?.oCache?.jentis_raw_controller_documentids}`,
  );

  return '';
}

/**
 * Reads the stored Jentis consent vendors and returns them as a JSON string.
 *
 * Returns `undefined` when Jentis is unavailable or the stored data is missing, empty or not valid JSON.
 */
export function getJentisConsent(): string | undefined {
  if (!isJentis()) return;

  const vendors = window.jentis?.helper?.getLocalStorageItem?.('jentis.consent.data')?.vendors;

  if (!vendors) return;

  if (typeof vendors === 'string') {
    return isJsonString(vendors) ? vendors : undefined;
  }

  if (typeof vendors === 'object' && Object.keys(vendors).length > 0) {
    return JSON.stringify(vendors);
  }

  return;
}

/**
 * Builds the Jentis data sent with the order. All three parts are required;
 * a partial payload would be rejected or mis-attributed, so it is omitted entirely.
 */
export function buildJentisPayloadData(): JentisPayloadData | undefined {
  const jentisUserId = getJentisUserId();
  const jentisSessionId = getJentisSessionId();
  const jentisConsent = getJentisConsent();

  if (!jentisUserId || !jentisSessionId || !jentisConsent) return;

  return { jentisUserId, jentisSessionId, jentisConsent };
}

export const isPageProductDetail = (): boolean | undefined => {
  if (!isBrowser()) return;

  return PixumEnvUtils.get('JENTIS')?.page.type === JENTIS_PAGE_TYPES.productDetail;
};
